import { FC } from 'react';
import { Input, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Form from '~/components/WizardForm';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import Section from '~/layouts/Section ';
import CustomFields from '~/components/CustomFields';
import { useGetApplication } from '~/controllers/wizard';
import { Checkbox, CountryInput, EmailFormItem, PhoneNumberFormItem } from '~/components';
import LocationInput from '~/components/inputs/LocationInput';
import { Stage } from '~/types/Application';
import phoneValidation from '~/utils/phoneValidation';
import emailValidation from '~/utils/emailValidation';
import ViewLocation from './components/ViewLocation';
import WizardContinueButton from '~/layouts/WizardContinueButton';
import { useLocation } from 'react-router';

const n = (namePath: string | string[]) => ['address'].concat([namePath].flat());

const Address: FC = () => {
  const { t } = useTranslation('ksaApplication');
  const { data: applicationQuery } = useGetApplication();
  const [form] = Form.useForm();

  const isManualAddressEnabled = Form.useWatch(manualAddressEnabledNamePath, form);

  if (!applicationQuery) return null;
  const location = useLocation();
  const geolocation = applicationQuery.payload?.geolocation;
  const initialAddressValues = applicationQuery.payload?.provided?.address;
  return (
    <Container className="Address">
      <Form payloadKey="address" stage={Stage.ksaApplication} form={form}>
        <Typography.Title>{t('Address')}</Typography.Title>
        <Typography.Paragraph>
          {t('This must be the location where you will take payments and where we can reach you.')}
        </Typography.Paragraph>
        <Section />
        <Section formItemLast>
          <InputsGroupLayout>
            <Form.Item label={t('Country')} name={n(['country'])}>
              <CountryInput />
            </Form.Item>
            <Form.Item label={t('Location')} name={n(['location'])}>
              <LocationInput />
            </Form.Item>
            <Form.Item label={t('City')} name={n(['city'])}>
              <Input />
            </Form.Item>
            <Form.Item label={t('Address line')} name={n(['addressLine'])}>
              <Input />
            </Form.Item>
            <Form.Item
              label={t('Office phone number')}
              name={n(['officePhoneNumber'])}
              initialValue={initialAddressValues?.contactInfo?.phoneNo}
            >
              <Input />
            </Form.Item>
            <Form.Item label={t('Fax number')} name={n(['faxNumber'])}>
              <Input />
            </Form.Item>
            <Form.Item
              label={t('Additional phone number')}
              name={n(['additionalPhoneNumber'])}
              initialValue={initialAddressValues?.contactInfo?.mobileNo}
            >
              <Input />
            </Form.Item>
            <Form.Item label={t('Additional fax number')} name={n(['additionalFaxNumber'])}>
              <Input />
            </Form.Item>
            <Form.Item label={t('P.O. Box')} name={n(['poBox'])}>
              <Input />
            </Form.Item>
            <Form.Item
              label={t('Company email')}
              name={n(['email'])}
              initialValue={initialAddressValues?.contactInfo?.email}
            >
              <Input />
            </Form.Item>
          </InputsGroupLayout>
        </Section>
        <Section formItemLast>
          <Form.Item name={manualAddressEnabledNamePath} valuePropName="checked">
            <Checkbox>{t('I want to change a business address')}</Checkbox>
          </Form.Item>
          {!isManualAddressEnabled && (
            <Typography.Paragraph>
              {t(
                'If your address details are incorrect or incomplete, check this box to manually enter the correct information.'
              )}
            </Typography.Paragraph>
          )}
          <Form.Visible name={manualAddressEnabledNamePath}>
            <InputsGroupLayout>
              <Form.Item
                label={t('City')}
                name={n(['manual', 'city'])}
                initialValue={initialAddressValues?.city}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label={t('Address line')}
                name={n(['manual', 'addressLine'])}
                initialValue={initialAddressValues?.addressLine}
              >
                <Input />
              </Form.Item>
              <PhoneNumberFormItem
                label={t('Office phone number')}
                name={n(['manual', 'officePhoneNumber'])}
                rules={[{ required: true }, { validator: phoneValidation }]}
                initialValue={initialAddressValues?.officePhoneNumber}
              />
              <Form.Item
                label={t('P.O. Box')}
                name={n(['manual', 'poBox'])}
                initialValue={initialAddressValues?.poBox}
              >
                <Input />
              </Form.Item>
              <EmailFormItem
                label={t('Company email')}
                rules={[{ required: true }, { validator: emailValidation }]}
                name={n(['manual', 'email'])}
                initialValue={initialAddressValues?.email}
              />
            </InputsGroupLayout>
          </Form.Visible>
        </Section>
        <Section formItemLast>
          {geolocation && <ViewLocation id="viewLocation" value={geolocation} />}
        </Section>
        <CustomFields />
        <WizardContinueButton id="wizardContinueButtonConfirm" formName={location.pathname} />
      </Form>
    </Container>
  );
};

export const manualAddressEnabledNamePath = n(['manual', 'enabled']);

const Container = styled.div`
  .Address-Checkbox {
    margin-bottom: 16px;
  }
`;

export default Address;
